import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthModalContext } from "../App";
import { useUser } from "../context/UserContext";
import CourseCard from "../components/common/CourseCard";
import EnrollModal from "../components/common/EnrollModal";
import { Footer } from "../components/common/SharedSections";
import { fetchCourses, fetchCategories } from "../services/api";

// ─── Price ranges (Step 4) ───────────────────────────────────────────────────
const PRICE_RANGES = [
  { label: "🆓 Free only", val: "free", min: 0, max: 0 },
  { label: "💸 Under ₹1,000", val: "u1k", min: 1, max: 999 },
  { label: "💳 ₹1,000 – ₹5,000", val: "1k5k", min: 1000, max: 5000 },
  { label: "💳 ₹5,000 – ₹15,000", val: "5k15k", min: 5000, max: 15000 },
  { label: "💎 ₹15,000 – ₹30,000", val: "15k30k", min: 15000, max: 30000 },
  { label: "🏅 ₹30,000 – ₹50,000", val: "30k50k", min: 30000, max: 50000 },
  { label: "🎓 Any – quality matters", val: "any", min: 0, max: 999999 },
];

// ─── Static questions (step 2, 3, 5 unchanged) ───────────────────────────────
const STATIC_QUESTIONS = {
  level: {
    q: "What's your experience level?",
    sub: "Be honest — we'll find the right course for where you are.",
    key: "level",
    opts: [
      { label: "🌱 Complete Beginner", val: "Beginner" },
      { label: "📈 Some Experience", val: "Intermediate" },
      { label: "🚀 Advanced / Expert", val: "Advanced" },
    ],
  },
  goal: {
    q: "What's your goal?",
    sub: "Tell us what you want to achieve.",
    key: "goal",
    opts: [
      { label: "💼 Get a new job", val: "job" },
      { label: "💰 Get a salary hike", val: "hike" },
      { label: "🔄 Switch careers", val: "switch" },
      { label: "📚 Learn for fun/growth", val: "growth" },
      { label: "🚀 Start a business", val: "business" },
    ],
  },
  time: {
    q: "How much time can you commit?",
    sub: "This helps us find the right course length.",
    key: "time",
    opts: [
      { label: "⚡ 1-5 hrs/week", val: "low" },
      { label: "🕐 5-10 hrs/week", val: "mid" },
      { label: "🔥 10+ hrs/week", val: "high" },
    ],
  },
};

// Step indices: 0=field, 1=level, 2=goal, 3=price, 4=time
const TOTAL_STEPS = 5;

const FinderPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const authCtx = useContext(AuthModalContext);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [enrollCourse, setEnrollCourse] = useState(null);

  const handleEnroll = (course) => {
    if (!isLoggedIn) {
      authCtx?.openAuthModal("login");
      return;
    }
    setEnrollCourse(course);
  };

  // Step 0 – backend categories
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);

  useEffect(() => {
    fetchCategories()
      .then((res) => {
        const cats = res.data?.data || res.data || [];
        setCategories(cats);
      })
      .catch(() => setCategories([]))
      .finally(() => setCatLoading(false));
  }, []);

  // ─── Handle selection for each step ────────────────────────────────────────
  const handleSelect = async (key, val) => {
    const newAnswers = { ...answers, [key]: val };
    setAnswers(newAnswers);

    if (step < TOTAL_STEPS - 1) {
      setStep((s) => s + 1);
    } else {
      // Last step → fetch results
      await loadResults(newAnswers);
    }
  };

  const loadResults = async (ans) => {
    setLoading(true);
    try {
      const priceRange =
        PRICE_RANGES.find((p) => p.val === ans.price) ||
        PRICE_RANGES[PRICE_RANGES.length - 1];

      // Build params for API
      const params = { limit: 12 };
      if (ans.field) params.cat = ans.field;
      if (ans.level) params.level = ans.level;

      // Price filter: pass min/max so backend can filter
      // Backend currently supports price='Free' or price='Paid'
      // We send both online + offline results by NOT filtering on 'classes'
      if (priceRange.val === "free") {
        params.price = "Free";
      } else if (priceRange.val !== "any") {
        params.price = "Paid";
        params.priceMin = priceRange.min;
        params.priceMax = priceRange.max;
      }

      const res = await fetchCourses(params);
      let data = res.data.data || [];

      // Client-side price filter
      if (priceRange.val === "free") {
        // Sirf wo courses jinka price exactly 0 ho
        data = data.filter((c) => (c.price ?? 0) === 0);
      } else if (priceRange.val !== "any") {
        // Paid range: online ya offline price range mein ho
        data = data.filter((c) => {
          const onlinePrice = c.price ?? 0;
          const offlinePrice = c.offPrice ?? 0;
          const inOnline =
            onlinePrice >= priceRange.min && onlinePrice <= priceRange.max;
          const inOffline =
            offlinePrice > 0 &&
            offlinePrice >= priceRange.min &&
            offlinePrice <= priceRange.max;
          return inOnline || inOffline;
        });
      }

      setResults(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setDone(true);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResults([]);
    setDone(false);
  };

  // ─── Match score ────────────────────────────────────────────────────────────
  const getMatch = (course) => {
    let score = 65;
    if (answers.field && course.catId === answers.field) score += 20;
    if (answers.level && course.level === answers.level) score += 10;
    const priceRange = PRICE_RANGES.find((p) => p.val === answers.price);
    if (priceRange) {
      const onlineOk =
        course.price >= priceRange.min && course.price <= priceRange.max;
      const offlineOk =
        (course.offPrice ?? 0) >= priceRange.min &&
        (course.offPrice ?? 0) <= priceRange.max;
      if (onlineOk || offlineOk) score += 5;
    }
    return Math.min(score, 99);
  };

  // ─── Progress ───────────────────────────────────────────────────────────────
  const progress = (step / TOTAL_STEPS) * 100;

  // ─── Render current step ────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      // STEP 0 – Field (from backend)
      case 0:
        return (
          <>
            <div className="quiz-q">What field do you want to learn?</div>
            <div className="quiz-sub">
              Choose the domain that excites you most.
            </div>

            {catLoading ? (
              <div className="loading">
                <div className="spinner" />
              </div>
            ) : (
              <div className="quiz-options">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className="quiz-opt"
                    onClick={() => handleSelect("field", cat.id)}
                  >
                    {cat.emoji || "📚"} {cat.name}
                  </button>
                ))}
              </div>
            )}
          </>
        );

      // STEP 1 – Experience level
      case 1: {
        const q = STATIC_QUESTIONS.level;
        return (
          <>
            <div className="quiz-q">{q.q}</div>
            <div className="quiz-sub">{q.sub}</div>
            <div className="quiz-options">
              {q.opts.map((opt) => (
                <button
                  key={opt.val}
                  className="quiz-opt"
                  onClick={() => handleSelect(q.key, opt.val)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        );
      }

      // STEP 2 – Goal
      case 2: {
        const q = STATIC_QUESTIONS.goal;
        return (
          <>
            <div className="quiz-q">{q.q}</div>
            <div className="quiz-sub">{q.sub}</div>
            <div className="quiz-options">
              {q.opts.map((opt) => (
                <button
                  key={opt.val}
                  className="quiz-opt"
                  onClick={() => handleSelect(q.key, opt.val)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        );
      }

      // STEP 3 – Budget / Price range (₹1,000 – ₹50,000)
      case 3:
        return (
          <>
            <div className="quiz-q">What's your budget?</div>
            <div className="quiz-sub">
              We'll show you courses (online &amp; offline) that fit your price
              range.
            </div>
            <div className="quiz-options">
              {PRICE_RANGES.map((pr) => (
                <button
                  key={pr.val}
                  className="quiz-opt"
                  onClick={() => handleSelect("price", pr.val)}
                >
                  {pr.label}
                </button>
              ))}
            </div>
          </>
        );

      // STEP 4 – Time commitment
      case 4: {
        const q = STATIC_QUESTIONS.time;
        return (
          <>
            <div className="quiz-q">{q.q}</div>
            <div className="quiz-sub">{q.sub}</div>
            <div className="quiz-options">
              {q.opts.map((opt) => (
                <button
                  key={opt.val}
                  className="quiz-opt"
                  onClick={() => handleSelect(q.key, opt.val)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        );
      }

      default:
        return null;
    }
  };

  // ─── Main render ────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="quiz-wrap">
        {!done ? (
          <>
            <div>
              <div className="progress-bar-wrap">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="progress-label">
                Step {step + 1} of {TOTAL_STEPS}
              </div>
            </div>

            {renderStep()}

            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                style={{
                  marginTop: 20,
                  background: "none",
                  border: "none",
                  color: "var(--text3)",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                ← Back
              </button>
            )}
          </>
        ) : (
          <div>
            <div style={{ marginBottom: 28 }}>
              <div className="progress-bar-wrap">
                <div className="progress-bar-fill" style={{ width: "100%" }} />
              </div>
              <div className="progress-label">✅ Complete!</div>
            </div>

            <div
              style={{
                fontFamily: "Syne,sans-serif",
                fontSize: "clamp(22px,4vw,32px)",
                fontWeight: 800,
                marginBottom: 8,
              }}
            >
              🎯 Your Personalized Matches
            </div>
            <p
              style={{ color: "var(--text2)", fontSize: 14, marginBottom: 28 }}
            >
              Based on your answers, here are the courses we recommend for you.
            </p>

            {/* Selected filters summary */}
            {answers.field || answers.price ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 20,
                }}
              >
                {answers.field &&
                  categories.find((c) => c.id === answers.field) && (
                    <span
                      style={{
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        borderRadius: 100,
                        padding: "4px 12px",
                        fontSize: 12,
                        color: "var(--text2)",
                      }}
                    >
                      {categories.find((c) => c.id === answers.field)?.emoji}{" "}
                      {categories.find((c) => c.id === answers.field)?.name}
                    </span>
                  )}
                {answers.price && (
                  <span
                    style={{
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: 100,
                      padding: "4px 12px",
                      fontSize: 12,
                      color: "var(--text2)",
                    }}
                  >
                    {PRICE_RANGES.find((p) => p.val === answers.price)?.label}
                  </span>
                )}
                {answers.level && (
                  <span
                    style={{
                      background: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: 100,
                      padding: "4px 12px",
                      fontSize: 12,
                      color: "var(--text2)",
                    }}
                  >
                    {
                      STATIC_QUESTIONS.level.opts.find(
                        (o) => o.val === answers.level,
                      )?.label
                    }
                  </span>
                )}
              </div>
            ) : null}

            {loading ? (
              <div className="loading">
                <div className="spinner" />
              </div>
            ) : results.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  color: "var(--text2)",
                }}
              >
                <div style={{ fontSize: 40, marginBottom: 12 }}>😕</div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  No courses found for this filter
                </div>
                <p style={{ fontSize: 13 }}>
                  Try a different budget range or browse all courses.
                </p>
              </div>
            ) : (
              <div className="courses-grid">
                {results.map((c) => (
                  <div key={c.id} style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        top: -10,
                        left: 12,
                        zIndex: 10,
                        background: "var(--accent)",
                        color: "white",
                        borderRadius: 100,
                        padding: "3px 10px",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {getMatch(c)}% Match
                    </div>
                    <CourseCard course={c} onEnroll={handleEnroll} />
                  </div>
                ))}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 28,
                flexWrap: "wrap",
              }}
            >
              <button className="btn-secondary" onClick={reset}>
                🔄 Retake Quiz
              </button>
              <button
                className="btn-primary"
                onClick={() => navigate("/explore")}
              >
                Browse All Courses →
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
      {enrollCourse && (
        <EnrollModal
          course={enrollCourse}
          onClose={() => setEnrollCourse(null)}
        />
      )}
    </div>
  );
};

export default FinderPage;
